import i18n from '@/i18n';
import { createContext, useContext, useEffect, useState, useRef } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationPath } from '@/domain/notification-links';
import { socketService } from '../services/socket.service';
import { useAuth } from '@/contexts/AuthContext';
import { notificationsApi } from '../api/notifications.api';
import type { Notification } from '../types/notification.types';
import { NotificationToastContainer } from '../components/notifications/NotificationToastContainer';
import { PatientCalledModal } from '../components/notifications/PatientCalledModal';
import { PaymentPendingModal } from '../components/notifications/PaymentPendingModal';
import { ReceptionistCalledModal } from '../components/notifications/ReceptionistCalledModal';
import { XrayUploadedModal } from '../components/notifications/XrayUploadedModal';
import {
  requestFcmToken,
  registerFcmToken,
  onForegroundMessage,
} from '../services/fcm.service';
import { playNotificationSound } from '../utils/notificationSound';
import { startRinging, stopRinging } from '../utils/ringSound';
import {
  isQuietAt,
  URGENT_TYPES,
  useMyNotificationPrefs,
} from '@/hooks/useMyNotificationPrefs';
import { setVolume } from '@/utils/audioState';

interface RingModalData {
  notificationId: number;
  patientName: string;
  doctorName: string;
  branchName?: string;
}

export interface XrayEvent {
  patientId: string;
  patientName: string;
  reservationId: string | null;
  imageCount: number;
  signedUrls: string[];
  photoIds?: string[];
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (notification: Notification) => void;
  markAsRead: (id: number) => void;
  /** Marks it read and goes to the page it names. Every click path uses it. */
  openNotification: (
    notification: Notification,
    options?: { replace?: boolean },
  ) => void;
  markAllAsRead: () => void;
  clearNotifications: () => void;
  setOnNewNotification: (
    callback: ((notification: Notification) => void) | undefined,
  ) => void;
  lastXrayEvent: XrayEvent | null;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

interface NotificationProviderProps {
  children: ReactNode;
  userId?: string;
  clinicId?: string;
}

interface ToastNotification {
  id: string;
  notification: Notification;
}

export const NotificationProvider = ({
  children,
  userId,
  clinicId,
}: NotificationProviderProps) => {
  const { user, clinic } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [toasts, setToasts] = useState<ToastNotification[]>([]);
  const [xrayNotification, setXrayNotification] = useState<{
    patientName: string;
    imageCount: number;
    signedUrls: string[];
    photoIds?: string[];
    uploadedAt: string;
  } | null>(null);
  const [lastXrayEvent, setLastXrayEvent] = useState<XrayEvent | null>(null);
  const [patientCalledData, setPatientCalledData] =
    useState<RingModalData | null>(null);
  const [paymentPendingData, setPaymentPendingData] =
    useState<RingModalData | null>(null);
  const [receptionistCalledId, setReceptionistCalledId] = useState<
    number | null
  >(null);
  /**
   * Unread notices as the server counts them. The badge used to count the
   * twenty rows the bell had loaded, so a busy morning read "20" at most and
   * a notice that scrolled off the list stopped counting.
   */
  const [serverUnread, setServerUnread] = useState(0);
  const onNewNotificationCallbackRef = useRef<
    ((notification: Notification) => void) | undefined
  >(undefined);
  const notificationTimeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(
    new Set(),
  );
  // Ids already shown, by either channel. The socket and a foreground push
  // carry the same notice; whichever lands second is not shown again.
  const socketDeliveredIdsRef = useRef<Set<number>>(new Set());
  const dispatchRef = useRef<(notification: Notification) => void>(() => {});

  /**
   * Quiet hours and sounds are this user's own, not the clinic's.
   *
   * They used to be read straight off `clinic.notificationSettings`,
   * which meant one owner's 22:00–08:00 window muted every other logged-in
   * user — including a night receptionist who needs the patient-called ring.
   * The clinic value is still passed, but only to seed a device that
   * has no preferences of its own yet.
   */
  const { prefs } = useMyNotificationPrefs(
    userId,
    clinicId,
    clinic?.notificationSettings,
  );
  const quietHours = prefs.quietHours;
  const soundSettings = prefs.sounds;

  // Keep the shared audio gain in step with this user's volume/mute, so a
  // preference set in Settings applies to alerts that arrive anywhere.
  useEffect(() => {
    setVolume(soundSettings.muted ? 0 : soundSettings.volume);
  }, [soundSettings.muted, soundSettings.volume]);

  // Request browser notification permission + register FCM token
  useEffect(() => {
    if (!userId) return;

    const setupFcm = async () => {
      const token = await requestFcmToken();
      if (token) {
        await registerFcmToken(token, i18n.language);
      }
    };

    setupFcm();
    // Pushes are worded in the device's language: tell the server when the
    // user switches it.
    const onLanguage = (lng: string) => {
      const token = localStorage.getItem('fcm_token');
      if (token) void registerFcmToken(token, lng);
    };
    i18n.on('languageChanged', onLanguage);
    return () => {
      i18n.off('languageChanged', onLanguage);
    };
  }, [userId]);

  // A push clicked while the app is open: the service worker hands over its
  // deep link instead of opening a second tab. Same-origin paths only.
  useEffect(() => {
    const sw = navigator.serviceWorker;
    if (!sw) return;
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'FCM_NOTIFICATION_CLICK') return;
      try {
        const url = new URL(event.data.url ?? '/', window.location.origin);
        if (url.origin !== window.location.origin) return;
        navigate(url.pathname + url.search);
      } catch {
        // malformed — ignore
      }
    };
    sw.addEventListener('message', onMessage);
    return () => sw.removeEventListener('message', onMessage);
  }, [navigate]);

  // Fetch initial notifications + subscribe to real-time socket updates
  useEffect(() => {
    if (!userId) return;

    let isSubscribed = true;

    const fetchNotifications = async () => {
      if (!isSubscribed) return;
      try {
        const notifPromises: Promise<Notification[]>[] = [
          notificationsApi.getUserNotifications(20, 0),
        ];
        if (clinicId) {
          notifPromises.push(notificationsApi.getClinicNotifications(20, 0));
        }

        const [results, unread] = await Promise.all([
          Promise.all(notifPromises),
          notificationsApi.getUnreadCount().catch(() => null),
        ]);
        const allNotifications = results.flat();

        const sorted = allNotifications.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        const unique = Array.from(
          new Map(sorted.map((n) => [n.id, n])).values(),
        );

        if (isSubscribed) {
          unique.forEach((n) => socketDeliveredIdsRef.current.add(n.id));
          setNotifications(unique.slice(0, 20));
          if (unread !== null) setServerUnread(unread);
        }
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };

    fetchNotifications();

    // Connect to notification socket (singleton — reuses existing connection)
    const socket = socketService.connect(userId, clinicId);

    // Whatever arrived while the socket was down (a laptop asleep, a phone
    // in a pocket, a server restart) is fetched when it comes back, and
    // when the tab is opened again. It used to appear only after a reload.
    let connectedBefore = socket.connected;
    const handleReconnect = () => {
      // The fetch above already covers the first connection.
      if (!connectedBefore) {
        connectedBefore = true;
        return;
      }
      void fetchNotifications();
    };
    socket.on('connect', handleReconnect);
    const handleVisible = () => {
      if (document.visibilityState === 'visible') void fetchNotifications();
    };
    document.addEventListener('visibilitychange', handleVisible);

    const handleNotification = (notification: Notification) => {
      // Already shown by a foreground push: keep the stored row (it carries
      // the wording data the push lacked), without a second toast.
      if (socketDeliveredIdsRef.current.has(notification.id)) {
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notification.id
              ? { ...notification, isRead: n.isRead }
              : n,
          ),
        );
        return;
      }
      // An x-ray upload opens its own popup instead of a toast, and still
      // lands in the list so the bell can take the user to the images.
      if (notification?.type === 'xray_uploaded') {
        socketDeliveredIdsRef.current.add(notification.id);
        setNotifications((prev) => [notification, ...prev].slice(0, 50));
        setServerUnread((c) => c + 1);
        try {
          // `live` carries the popup data; an API from before it sent the
          // same data JSON-encoded in the message.
          const data = (notification.live ??
            JSON.parse(notification.message)) as Partial<XrayEvent>;
          const patientName =
            data.patientName ?? i18n.t('notifications.fallback.patient');
          setXrayNotification({
            patientName,
            imageCount: data.imageCount ?? 1,
            signedUrls: data.signedUrls ?? [],
            photoIds: data.photoIds ?? [],
            // The wire value is an ISO string whatever the type says.
            uploadedAt: new Date(
              notification.createdAt ?? Date.now(),
            ).toISOString(),
          });
          setLastXrayEvent({
            patientId: data.patientId ?? '',
            patientName,
            reservationId: data.reservationId ?? null,
            imageCount: data.imageCount ?? 1,
            signedUrls: data.signedUrls ?? [],
            photoIds: data.photoIds ?? [],
          });
        } catch {
          // malformed — ignore
        }
        return;
      }
      socketDeliveredIdsRef.current.add(notification.id);
      dispatchRef.current(notification);
    };

    socket.on('notification', handleNotification);

    const handleXrayUploaded = (data: {
      patientName: string;
      imageCount: number;
      signedUrls: string[];
      uploadedAt: string;
    }) => {
      setXrayNotification(data);
    };
    socket.on('xray_uploaded', handleXrayUploaded);

    // FCM foreground handler — only dispatch if Socket.IO didn't already deliver it
    const unsubFcm = onForegroundMessage(({ title, body, data }) => {
      // A push for another clinic this person works at: the device still
      // shows it while the app is closed, but it is not this bell's.
      if (data?.clinicId && clinicId && data.clinicId !== clinicId) return;
      const notifId = data?.notificationId
        ? Number(data.notificationId)
        : -Date.now();
      if (socketDeliveredIdsRef.current.has(notifId)) return;
      socketDeliveredIdsRef.current.add(notifId);

      let wording: Notification['i18n'] = null;
      try {
        wording = data?.i18n ? JSON.parse(data.i18n) : null;
      } catch {
        // Not data we can word: the stored text is shown.
      }

      const syntheticNotif: Notification = {
        id: notifId,
        title,
        message: body,
        type: (data?.type as Notification['type']) ?? 'system_message',
        isRead: false,
        createdAt: new Date(),
        updatedAt: new Date(),
        userId: userId,
        clinicId: clinicId,
        resourceId: data?.resourceId,
        resourceType: data?.resourceType,
        i18n: wording,
      };
      dispatchRef.current(syntheticNotif);
    });

    // The set itself, not the ref: the cleanup must clear the timers this
    // effect run could have started.
    const timeouts = notificationTimeoutsRef.current;

    return () => {
      isSubscribed = false;
      socket.off('notification', handleNotification);
      socket.off('xray_uploaded', handleXrayUploaded);
      socket.off('connect', handleReconnect);
      document.removeEventListener('visibilitychange', handleVisible);
      socketService.disconnect();
      unsubFcm?.();
      timeouts.forEach((id) => clearTimeout(id));
      timeouts.clear();
    };
  }, [userId, clinicId]);

  const dispatchNotification = (notification: Notification) => {
    // Always persist to the in-memory list (cap at 50)
    setNotifications((prev) => [notification, ...prev].slice(0, 50));
    if (!notification.isRead) setServerUnread((c) => c + 1);

    /**
     * Quiet hours, with the urgent exception applied.
     *
     * `allowUrgent` exempts the two alerts that always mean a person is
     * physically waiting — a patient called to the chair, a doctor calling the
     * front desk. Suppressing those overnight is how someone stands unattended
     * in a corridor; suppressing a low-stock warning overnight is correct.
     */
    const inQuietWindow = isQuietAt(quietHours, new Date());
    const isUrgent = URGENT_TYPES.has(notification.type);
    const quiet = inQuietWindow && !(quietHours.allowUrgent && isUrgent);

    /** Master mute wins over every per-event sound toggle. */
    const canPlaySound = !quiet && !soundSettings.muted;

    if (notification.type === 'patient_called') {
      // Names come as data; the sentence is only parsed for notifications
      // written before the API sent them.
      const message = notification.message;
      const named = notification.i18n?.params;
      const patientMatch = message.match(/^(.+?) has been called/);
      const doctorMatch = message.match(/by Dr\. (.+)$/);

      setPatientCalledData({
        notificationId: notification.id,
        patientName:
          (named?.patient as string | null) ||
          patientMatch?.[1] ||
          i18n.t('notifications.fallback.patient'),
        doctorName:
          (named?.doctor as string | null) ||
          doctorMatch?.[1] ||
          i18n.t('notifications.fallback.doctor'),
        branchName: undefined,
      });

      if (canPlaySound && soundSettings.patientCalledSound !== false) {
        startRinging();
      }
    } else if (notification.type === 'payment_pending') {
      const message = notification.message;
      const named = notification.i18n?.params;
      const patientMatch = message.match(/^(.+?) has completed treatment/);
      const doctorMatch = message.match(/with Dr\. (.+?) and/);

      setPaymentPendingData({
        notificationId: notification.id,
        patientName:
          (named?.patient as string | null) ||
          patientMatch?.[1] ||
          i18n.t('notifications.fallback.patient'),
        doctorName:
          (named?.doctor as string | null) ||
          doctorMatch?.[1] ||
          i18n.t('notifications.fallback.doctor'),
        branchName: undefined,
      });

      if (canPlaySound && soundSettings.paymentPendingSound !== false) {
        startRinging();
      }
    } else if (notification.type === 'receptionist_called') {
      setReceptionistCalledId(notification.id);
      if (canPlaySound) {
        startRinging();
      }
    } else {
      if (!quiet) {
        // Toast — cap at 10 to prevent unbounded growth
        const toastId = `toast-${Date.now()}-${Math.random()}`;
        setToasts((prev) =>
          [...prev, { id: toastId, notification }].slice(-10),
        );

        if (canPlaySound && soundSettings.generalNotificationSound !== false) {
          playNotificationSound();
        }
      }
    }

    if (onNewNotificationCallbackRef.current) {
      onNewNotificationCallbackRef.current(notification);
    }
  };

  const addNotification = (notification: Notification) => {
    setNotifications((prev) => [notification, ...prev]);
  };

  /**
   * Read at once, here and on the server. The bell used to wait for the
   * server, and the server refused clinic-wide notices, so they stayed
   * unread however often they were opened. Negative ids are push-only
   * placeholders with no row behind them.
   */
  const markAsRead = async (id: number) => {
    const wasUnread = notifications.some((n) => n.id === id && !n.isRead);
    setNotifications((prev) =>
      prev.map((notif) =>
        notif.id === id ? { ...notif, isRead: true } : notif,
      ),
    );
    if (wasUnread) setServerUnread((c) => Math.max(0, c - 1));
    if (id < 0) return;
    try {
      await notificationsApi.markAsRead(id);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const openNotification = (
    notification: Notification,
    options?: { replace?: boolean },
  ) => {
    if (!notification.isRead) void markAsRead(notification.id);
    navigate(notificationPath(notification), options);
  };

  const markAllAsRead = async () => {
    try {
      await notificationsApi.markAllAsRead();
      setNotifications((prev) =>
        prev.map((notif) => ({ ...notif, isRead: true })),
      );
      setServerUnread(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const clearNotifications = () => {
    setNotifications([]);
  };

  const setOnNewNotification = (
    callback: ((notification: Notification) => void) | undefined,
  ) => {
    onNewNotificationCallbackRef.current = callback;
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const handlePatientCalledConfirm = async () => {
    if (patientCalledData) {
      await markAsRead(patientCalledData.notificationId);
      setPatientCalledData(null);
    }
  };

  const handlePaymentPendingConfirm = async () => {
    if (paymentPendingData) {
      await markAsRead(paymentPendingData.notificationId);
      setPaymentPendingData(null);
    }
  };

  const handleReceptionistCalledConfirm = async () => {
    if (receptionistCalledId) {
      await markAsRead(receptionistCalledId);
      setReceptionistCalledId(null);
    }
    stopRinging();
  };

  // Never below what the loaded list itself shows unread.
  const unreadCount = Math.max(
    serverUnread,
    notifications.filter((n) => !n.isRead).length,
  );

  void user;

  // The socket handlers are created once per sign-in but must act on
  // TODAY's quiet hours and sound settings, so they call through this ref,
  // which always holds the latest dispatch.
  useEffect(() => {
    dispatchRef.current = dispatchNotification;
  });

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        addNotification,
        markAsRead,
        openNotification,
        markAllAsRead,
        clearNotifications,
        setOnNewNotification,
        lastXrayEvent,
      }}
    >
      {children}
      <NotificationToastContainer
        toasts={toasts}
        onRemove={removeToast}
        onOpen={(id, notification) => {
          removeToast(id);
          openNotification(notification);
        }}
      />
      <PatientCalledModal
        isOpen={!!patientCalledData}
        patientName={patientCalledData?.patientName || ''}
        doctorName={patientCalledData?.doctorName || ''}
        branchName={patientCalledData?.branchName}
        onConfirm={handlePatientCalledConfirm}
      />
      <PaymentPendingModal
        isOpen={!!paymentPendingData}
        patientName={paymentPendingData?.patientName || ''}
        doctorName={paymentPendingData?.doctorName || ''}
        branchName={paymentPendingData?.branchName}
        onConfirm={handlePaymentPendingConfirm}
      />
      <ReceptionistCalledModal
        isOpen={!!receptionistCalledId}
        onConfirm={handleReceptionistCalledConfirm}
      />
      <XrayUploadedModal
        isOpen={!!xrayNotification}
        patientName={xrayNotification?.patientName ?? ''}
        imageCount={xrayNotification?.imageCount ?? 0}
        signedUrls={xrayNotification?.signedUrls ?? []}
        photoIds={xrayNotification?.photoIds ?? []}
        onClose={() => setXrayNotification(null)}
      />
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      'useNotifications must be used within NotificationProvider',
    );
  }
  return context;
};

export const useSafeNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    console.warn(
      '⚠️ useNotifications called outside NotificationProvider, returning stub',
    );
    return {
      notifications: [],
      unreadCount: 0,
      addNotification: () => {},
      markAsRead: async () => {},
      openNotification: () => {},
      markAllAsRead: async () => {},
      clearNotifications: () => {},
      setOnNewNotification: () => {},
    };
  }
  return context;
};
